import { useCallback, useEffect, useMemo, useState } from "react";
import { useFilteredAddCandidatesToGroupPagingQuery } from "../../../../App/Queries/CandidateQueries";
import { useSavedSearchesQuery } from "../../../../App/Queries/FilterQueries";
import { useAddMembersToGroupMutation, useGroupMemberIdsQuery } from "../../../../App/Queries/GroupQueries";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { useAsyncDebouncedHandler } from "../../../../App/State/Hooks/useDebouncedHandler";
import { useCandidatesFilterStore } from "../../../../App/State/Stores/CandidatesPaginationStore";
import { ICandidate } from "../../../../App/Typings/Candidate";
import { IAddMembersToGroup, IGroup } from "../../../../App/Typings/Groups";
import { STORAGE_VERSION_DATE } from "../../../../App/Utilities/browserStorage";
import { useAppSelector } from "../../../../App/State/Hooks/reduxHooks";
import { selectClientValue } from "../../../../App/State/Slices/SelectedClientSlice";

export type AddToGroupDialogStateType = ReturnType<typeof useAddToGroupDialogState>;

export function useAddToGroupDialogState(group: IGroup) {
  const { clientId } = useAppSelector(selectClientValue);
  const { displayError, displayWarning, displaySuccess } = useNotification();
  const { mutateAsync: addMembersToGroup, isLoading: isLoadingAddMembers } = useAddMembersToGroupMutation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [excludedCandidates, setExcludedCandidates] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const { data: savedSearchFilters } = useSavedSearchesQuery();
  const { filteredCandidateList, isLoading, totalItems, filterQueryString } =
    useFilteredAddCandidatesToGroupPagingQuery(dialogOpen, clientId);

  const {
    page,
    pageSize,
    sortBy,
    sortDirection,
    search,
    tags,
    timeFilter,
    handleSearchChange,
    handleTagsChange,
    handleTimeFilterChange,
    handleChangePage,
    handleChangePageSize,
    handleRequestSort,
  } = useCandidatesFilterStore(`add-to-group-${clientId}-${STORAGE_VERSION_DATE}`);

  const { data: filteredGroupMemberIds = [], isLoading: filteredMemberListIsLoading } =
    useGroupMemberIdsQuery(group.id, filterQueryString, dialogOpen);

  const disableSelectAll = useMemo(
    () => {
      const availableCount =
        filteredCandidateList && totalItems > 0
          ? totalItems - excludedCandidates.length - filteredGroupMemberIds.length
          : 0;
      return availableCount === 0;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filteredCandidateList.length,
      totalItems,
      excludedCandidates.length,
      filteredGroupMemberIds.length,
      filterQueryString,
    ],
  );

  const handleGroupSave = useCallback(async () => {
    if (selectedCandidates.length === 0 && !selectAll) {
      displayWarning("No candidates selected to add to group.");
      return;
    }

    const command = {
      groupId: group.id,
      selectAll,
      candidateIds: selectedCandidates,
      excludedIds: excludedCandidates,
    } as IAddMembersToGroup;

    if (timeFilter.value) {
      command.daysSubmitted = timeFilter.value;
    }
    if (search) {
      command.search = search;
    }
    if (tags.length > 0) {
      command.tags = tags;
    }

    await addMembersToGroup(command, {
      onSettled: (response, error) => {
        if (error || !response) {
          displayError(
            typeof error === "string"
              ? error
              : "There was an error adding candidates to the group. Please try again.",
          );
          return;
        }
        if (response.success) {
          displaySuccess(response.message ?? "Candidates added to group successfully!");
          handleClose();
        } else {
          displayWarning(response.message ?? "There was an error adding candidates to the group.");
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id, selectedCandidates, excludedCandidates, selectAll, timeFilter.value, search, tags.length]);

  const handleSaveClick = useAsyncDebouncedHandler(
    async (handler: () => Promise<void>) => await handler(),
    500,
  );

  const isCandidateSelected = useCallback(
    (row: ICandidate) => {
      if (selectAll) {
        return !excludedCandidates.includes(row.id) && !filteredGroupMemberIds.includes(row.id);
      }
      return selectedCandidates.includes(row.id);
    },
    [selectAll, excludedCandidates, filteredGroupMemberIds, selectedCandidates],
  );

  const isRowDisabled = useCallback(
    (row: ICandidate) => filteredGroupMemberIds.includes(row.id),
    [filteredGroupMemberIds],
  );

  const handleDialogOpen = () => setDialogOpen(true);

  const handleClearSelected = () => {
    setSelectedCandidates([]);
    setExcludedCandidates([]);
    setSelectAll(false);
    setSelectedCount(0);
  };

  const handleClose = () => {
    setDialogOpen(false);
    handleClearSelected();
  };

  const handleDeselectCandidate = useCallback(
    (row: ICandidate) => {
      const newSelected = selectedCandidates.filter((id) => id !== row.id);
      const countChange = selectedCandidates.length - newSelected.length;
      setSelectedCandidates(newSelected);
      setSelectedCount((count) => count - countChange);
      if (selectAll) {
        setExcludedCandidates((excluded) => [...excluded, row.id]);
      }
    },
    [selectedCandidates, selectAll],
  );

  const handleSelectCandidate = useCallback(
    (row: ICandidate) => {
      setSelectedCandidates((prev) => [...prev, row.id]);
      setSelectedCount((count) => count + 1);
      if (selectAll) {
        setExcludedCandidates((excluded) => excluded.filter((id) => id !== row.id));
      }
    },
    [selectAll],
  );

  const handleClickCandidateRow = useCallback(
    (row: ICandidate) => {
      if (selectedCandidates.includes(row.id)) {
        handleDeselectCandidate(row);
      } else {
        handleSelectCandidate(row);
      }
    },
    [selectedCandidates, handleDeselectCandidate, handleSelectCandidate],
  );

  const handleSelectAll = useCallback(() => {
    const selectedList =
      filteredCandidateList
        .filter((c) => !filteredGroupMemberIds.includes(c.id) && !excludedCandidates.includes(c.id))
        .map((c) => c.id) ?? [];
    const newCount =
      !isLoading && totalItems > 0
        ? totalItems - excludedCandidates.length - filteredGroupMemberIds.length
        : 0;
    setSelectedCandidates(selectedList);
    setSelectAll(true);
    setSelectedCount(newCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, totalItems, excludedCandidates, filteredGroupMemberIds, filterQueryString]);

  const handleSelectAllClick = useCallback(
    (selected: boolean) => {
      if (selected) {
        handleSelectAll();
      } else {
        handleClearSelected();
      }
    },
    [handleSelectAll],
  );

  useEffect(() => {
    if (!isLoading && !filteredMemberListIsLoading && disableSelectAll) {
      // If the select all button becomes disabled, clear the selected candidates
      handleClearSelected();
    }
  }, [filterQueryString, isLoading, filteredMemberListIsLoading, disableSelectAll]);

  useEffect(() => {
    if (!isLoading && !filteredMemberListIsLoading && selectAll) {
      // If paging or filtering changes, re-select all candidates
      handleSelectAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSelectAll, filterQueryString, isLoading, filteredMemberListIsLoading]);

  return {
    getters: {
      candidateList: filteredCandidateList,
      dialogOpen,
      disableSelectAll,
      filteredCandidateListIsLoading: isLoading,
      filterQueryString,
      isCandidateSelected,
      isRowDisabled,
      isLoadingAddMembers,
      tags,
      timeFilter,
      totalItems,
      savedSearchFilters,
      selectAll,
      selectedCandidates,
      selectedCount,
      excludedCandidates,
      page,
      pageSize,
      sortBy,
      sortDirection,
      search,
    },
    setters: {
      setDialogOpen,
    },
    handlers: {
      handleClickCandidateRow,
      handleDialogOpen,
      handleClose,
      handleSave: handleGroupSave,
      handleSaveClick,
      handleSelectAllClick,
      handleChangePage,
      handleChangePageSize,
      handleRequestSort,
      handleSearchChange,
      handleTagsChange,
      handleTimeFilterChange,
    },
  } as const;
}
